import { DeliveryType, UiLabel } from 'hx-services';
import { ClientKind, DiscountToggle, EditorChannel } from '@callcenter-app/service/order-editor.service';
import { cleanCompanyDraft, CompanyDraft } from './company';
import { cleanRejectionDraft, isEmptyRejection, RejectionDraft } from './rejections';

/**
 * The draft of an order the operator is putting together (design §5.3): everything the form holds that is neither in the address of
 * the page nor on the server — the client and the recipient, «привязать звонок», the chosen lines (with the title and the price of the
 * moment), the way of receiving, the discounts switched off. A draft belongs to ONE conversation and ONE operator: its key carries the
 * operator's id and the draft's own id (`?d=`), it lives 30 minutes, and only a reload the browser reports as `reload` or a move inside the conversation brings it back
 * silently — a way in by the browser's history asks first. (On the stand an F5 reaches the page as `navigate`: the sign-in through Keycloak is a redirect, which the browser
 * does not call a reload — so there the operator usually meets the plaque «Продолжить / Начать новый» after F5, and that is acceptable: nothing is lost, one click.)
 * Pure; `order-draft.store.ts` keeps it in the tab's `sessionStorage`.
 *
 * A phone number and an address are personal data: the draft is never put in `localStorage` or in the address of the page.
 */

export const DRAFT_VERSION = 1;
/** A draft nobody touched for this long is gone — it is read as «no draft». */
export const DRAFT_TTL_MS = 30 * 60_000;
export const DRAFT_KEY_PREFIX = 'cc.orderEditor.';

export type DraftKind = 'new' | 'cart' | 'edit';

/**
 * The client or the recipient: the digits typed after the dial code and the country. NOT the name: a name goes to the server a second after it is typed and comes back with the answer
 * of E8 — a copy of it in the tab's storage would only be one more place a stranger's name can be read from (security 2a1-P3-11). The number stays: it brings the form back after a reload.
 */
export interface DraftParty {
  national: string;
  countryId?: number;
}

export interface DraftRecipient extends DraftParty {
  enabled: boolean;
}

/** One chosen variant: the title and the price are those of the moment of choice — the catalog may have moved on by the time the draft is read. */
export interface DraftLine {
  productInfoId: number;
  priceAmount: number;
  amount: number;
  title: UiLabel;
  /** Grams. */
  weight?: number;
  /** The price of one piece of the variant: a whole number of the currency, or the lowest of a range (`priceFrom`). */
  price?: number;
  priceFrom?: boolean;
}

/**
 * The delivery: the address and its details, the slot, the price chosen by hand (2a-2). The pickup store and time (`DraftReceiving.storeId` / `time`) are ANOTHER choice, and they
 * stay while the operator looks at the delivery: the switch «Самовывоз | Доставка» takes nothing away from the other way. The slot belongs to a store — `storeId` here.
 */
export interface DraftDelivery {
  address?: string;
  admDiv?: string;
  lat?: number;
  lng?: number;
  entrance?: string;
  floor?: string;
  flat?: string;
  note?: string;
  /** The name of a saved address of the client the address was taken from («Работа»); gone with the address when another is chosen. */
  alias?: string;
  /** The store of the chosen slot. */
  storeId?: number;
  rangeId?: number;
  manualPriceId?: number;
}

export interface DraftReceiving {
  type: DeliveryType;
  storeId?: number;
  /** The pickup time `HH:mm`; absent — not chosen. */
  time?: string;
  delivery?: DraftDelivery;
}

/**
 * What the operator wrote on step 2 of this conversation — the inscription, the note, the comment, the channel, «как узнал», the switches: it belongs to the CONVERSATION, not to the booking, so a
 * booking that is taken off («Снять бронь», «Бронь снята → Закрыть») and made again from step 1 has it back. Written when the operator goes back to step 1; read by the next booking.
 */
export interface CarriedTexts {
  decorText: string;
  requestDecor: boolean;
  note: string;
  comment: string;
  channel: EditorChannel;
  referrer?: string;
  attachReceipt: boolean;
  important: boolean;
  kaspiNow: boolean;
  clientType: ClientKind;
  /** «Кто заказывает» was decided (by the operator, or by the booking it was read from): the next booking of the conversation keeps `clientType` and does not take its own. Absent — nothing decided. */
  clientTypeSet?: true;
  /** What was typed into the forms of the legal entity and is not saved (2c). */
  company?: CompanyDraft;
}

const CARRIED_TEXT_LIMIT = 2000;
const CARRIED_REFERRER_LIMIT = 200;

export interface OrderDraft {
  version: typeof DRAFT_VERSION;
  /** The conversation's id — `?d=` of the address. */
  id: string;
  userId: number;
  /** Browser clock, ms: only the 30-minute term is measured with it, it is never printed. */
  touchedAt: number;
  client: DraftParty;
  recipient: DraftRecipient;
  /** «Привязать звонок»: absent — the default (on while the operator has a call). */
  callLinked?: boolean;
  lines: DraftLine[];
  receiving: DraftReceiving;
  /** The discounts the operator switched off — E7 `toggles`, all `enabled: false`. */
  disabledDiscounts: DiscountToggle[];
  /** The refusals of this conversation (2c): the record of E24 and what it holds. */
  rejection?: RejectionDraft;
  /** What step 2 of this conversation held when the operator came back to step 1 (see `CarriedTexts`). */
  step2?: CarriedTexts;
}

export function newDraft(id: string, userId: number, nowMs: number): OrderDraft {
  return {
    version: DRAFT_VERSION,
    id,
    userId,
    touchedAt: nowMs,
    client: { national: '' },
    recipient: { enabled: false, national: '' },
    lines: [],
    receiving: { type: 'PICKUP' },
    disabledDiscounts: [],
  };
}

/** The same draft, touched now — every change of the form renews the 30 minutes. */
export function touched(draft: OrderDraft, nowMs: number): OrderDraft {
  return { ...draft, touchedAt: nowMs };
}

export function isExpired(draft: Pick<OrderDraft, 'touchedAt'>, nowMs: number): boolean {
  return nowMs - draft.touchedAt >= DRAFT_TTL_MS;
}

/** The form holds a composition, an address, a recipient or a refusal — what a new client must not inherit by accident. */
export function hasFilledForm(draft: OrderDraft): boolean {
  const delivery = draft.receiving.delivery;
  return draft.lines.length > 0
    || !!delivery?.address
    || draft.recipient.enabled
    || draft.recipient.national.length > 0
    || !isEmptyRejection(draft.rejection);
}

/** Nothing at all is written: nothing to bring back, nothing to ask about. */
export function isBlank(draft: OrderDraft): boolean {
  return !hasFilledForm(draft)
    && draft.client.national.length === 0
    && draft.receiving.storeId === undefined
    && draft.receiving.time === undefined
    && draft.receiving.delivery === undefined
    && draft.disabledDiscounts.length === 0;
}

/**
 * The number of the client is replaced by another resolved one while the form is filled — the page must ask «Другой клиент — очистить» /
 * «Тот же клиент — оставить» (a typing error in the number is the same client). Not asked: the number only grows (the first number is
 * still being typed), nothing is filled, or the number is the one already resolved.
 */
export function needsClientChoice(draft: OrderDraft, resolvedPhone: string | undefined, nextPhone: string): boolean {
  return !!resolvedPhone && resolvedPhone !== nextPhone && hasFilledForm(draft);
}

/** «Другой клиент — очистить»: the composition, the receiving, the address, the recipient, the refusal and the switched-off discounts go; the new client stays. */
export function clearForNewClient(draft: OrderDraft, client: DraftParty): OrderDraft {
  return {
    ...draft,
    client,
    recipient: { enabled: false, national: '' },
    lines: [],
    receiving: { type: draft.receiving.type },
    disabledDiscounts: [],
    rejection: undefined,
    // the texts of step 2 were about the other client's order
    step2: undefined,
  };
}

/** «Тот же клиент — оставить»: only the number is replaced (the typing error is mended); everything else stays. */
export function keepForSameClient(draft: OrderDraft, client: DraftParty): OrderDraft {
  return { ...draft, client };
}

// ---------- keys ----------

export interface DraftKeyParts {
  userId: number;
  kind: DraftKind;
  id: string;
}

const KEY_RE = /^cc\.orderEditor\.(\d+)\.(new|cart|edit)\.([A-Za-z0-9_-]+)$/;

/** `cc.orderEditor.<userId>.new.<d>` — the operator and the conversation are in the key, so the draft of another operator or another conversation is never read by mistake. */
export function draftKey(userId: number, kind: DraftKind, id: string): string {
  return `${DRAFT_KEY_PREFIX}${userId}.${kind}.${id}`;
}

export function parseDraftKey(key: string): DraftKeyParts | undefined {
  const m = KEY_RE.exec(key);
  return m ? { userId: Number(m[1]), kind: m[2] as DraftKind, id: m[3] } : undefined;
}

/** Any key of the editor, whatever its operator — for the sweeps of the store (a malformed one is swept too). */
export function isEditorKey(key: string): boolean {
  return key.startsWith(DRAFT_KEY_PREFIX);
}

// ---------- how it comes back ----------

/** `silent` — the form is brought back as it was; `ask` — it is brought back behind the «Продолжить / Начать новый» plaque; `fresh` — a clean form. */
export type RestoreMode = 'silent' | 'ask' | 'fresh';

export interface RestoreContext {
  /** `performance.getEntriesByType('navigation')[0].type` — says what loaded the DOCUMENT; it means something only for the first navigation after the load. */
  documentNavigation?: string;
  /** The first navigation since the page was loaded. */
  firstNavigation: boolean;
  /** The router's `navigationTrigger`: `popstate` is the browser's «Назад» / «Вперёд». */
  trigger: 'imperative' | 'popstate' | 'hashchange';
  /** The navigation carries the conversation's mark with this very draft id — the editor's own move inside the conversation. */
  conversation: boolean;
  /** The draft of this id is in the storage, not expired, and has something in it. */
  restorable: boolean;
}

/**
 * Where a form comes from (§5.3): a document the browser reports as a `reload` and the editor's own moves inside the conversation («Снять бронь», «Изменить»,
 * «Назад» of step 2) bring it back silently. Anything else that arrives with an old `d` — the browser's «Назад», a new tab, a pasted
 * address, and an F5 that the sign-in redirect makes a `navigate` — gets the plaque, so that an operator who comes back by history never continues the order of the previous
 * client unaware.
 */
export function restoreMode(ctx: RestoreContext): RestoreMode {
  if (!ctx.restorable) {
    return 'fresh';
  }
  if (ctx.conversation) {
    return 'silent';
  }
  return ctx.firstNavigation && ctx.trigger === 'imperative' && ctx.documentNavigation === 'reload' ? 'silent' : 'ask';
}

/** «+7 ••• ••• 12-34» — the number on the plaque of a restored order: enough to recognise it, not enough to read it off the screen. */
export function maskedPhone(national: string, dialCode: string): string {
  if (national.length < 4) {
    return '';
  }
  const tail = national.slice(-4);
  return `${dialCode} ••• ••• ${tail.slice(0, 2)}-${tail.slice(2)}`;
}

// ---------- storage format ----------

export function serializeDraft(draft: OrderDraft): string {
  return JSON.stringify(draft);
}

type Dict = Record<string, unknown>;

function isDict(value: unknown): value is Dict {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function num(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function digits(value: unknown): string {
  return typeof value === 'string' ? value.replace(/\D/g, '').slice(0, 15) : '';
}

function optionalText(value: unknown, max: number): string | undefined {
  const text = str(value);
  return text !== undefined && text.length > 0 ? text.slice(0, max) : undefined;
}

function parseLabel(value: unknown): UiLabel {
  const label: UiLabel = {};
  if (isDict(value)) {
    (['ru', 'kk', 'en', 'uz', 'tg'] as const).forEach(lang => {
      const text = str(value[lang]);
      if (text !== undefined) {
        label[lang] = text.slice(0, 300);
      }
    });
  }
  return label;
}

function parseParty(value: unknown): DraftParty {
  const src = isDict(value) ? value : {};
  const countryId = num(src['countryId']);
  // a draft of an older build may carry a name: it is not read
  return { national: digits(src['national']), ...(countryId !== undefined ? { countryId } : {}) };
}

function parseLine(value: unknown): DraftLine | undefined {
  if (!isDict(value)) {
    return undefined;
  }
  const productInfoId = num(value['productInfoId']);
  const priceAmount = num(value['priceAmount']);
  const amount = num(value['amount']);
  if (productInfoId === undefined || priceAmount === undefined || priceAmount <= 0 || amount === undefined || !Number.isInteger(amount) || amount < 1) {
    return undefined;
  }
  const weight = num(value['weight']);
  const price = num(value['price']);
  return {
    productInfoId, priceAmount, amount, title: parseLabel(value['title']),
    ...(weight !== undefined ? { weight } : {}),
    ...(price !== undefined ? { price } : {}),
    ...(value['priceFrom'] === true ? { priceFrom: true } : {}),
  };
}

function parseDelivery(value: unknown): DraftDelivery | undefined {
  if (!isDict(value)) {
    return undefined;
  }
  const out: DraftDelivery = {};
  const texts: Array<['address' | 'admDiv' | 'alias' | 'entrance' | 'floor' | 'flat' | 'note', number]> = [
    ['address', 200], ['admDiv', 200], ['alias', 50], ['entrance', 50], ['floor', 50], ['flat', 50], ['note', 300],
  ];
  texts.forEach(([key, max]) => {
    const text = optionalText(value[key], max);
    if (text) {
      out[key] = text;
    }
  });
  (['lat', 'lng', 'storeId', 'rangeId', 'manualPriceId'] as const).forEach(key => {
    const n = num(value[key]);
    if (n !== undefined) {
      out[key] = n;
    }
  });
  return Object.keys(out).length ? out : undefined;
}

function parseReceiving(value: unknown): DraftReceiving {
  const src = isDict(value) ? value : {};
  const storeId = num(src['storeId']);
  const time = str(src['time']);
  const delivery = parseDelivery(src['delivery']);
  return {
    type: src['type'] === 'DELIVERY' ? 'DELIVERY' : 'PICKUP',
    ...(storeId !== undefined ? { storeId } : {}),
    ...(time && /^\d{2}:\d{2}$/.test(time) ? { time } : {}),
    ...(delivery ? { delivery } : {}),
  };
}

function parseToggle(value: unknown): DiscountToggle | undefined {
  if (!isDict(value)) {
    return undefined;
  }
  const promoId = num(value['promoId']);
  const priceId = num(value['priceId']);
  return promoId === undefined ? undefined : { promoId, ...(priceId !== undefined ? { priceId } : {}), enabled: false };
}

/**
 * The storage is the tab's own and nobody else writes to it — but a draft of an older build, a half-written value or a hand-edited one must
 * never break the page: anything that does not read as a draft of THIS version is «no draft». Every field is checked, cut to its limit and
 * given its default; the numbers of a phone are kept as digits only.
 */
/** The texts of step 2 that came back from the storage: every field checked and cut to its limit — a stored value is never trusted as it is. */
function parseCarried(value: unknown): CarriedTexts | undefined {
  if (!isDict(value)) {
    return undefined;
  }
  const text = (v: unknown): string => (typeof v === 'string' ? v.slice(0, CARRIED_TEXT_LIMIT) : '');
  const referrer = optionalText(value['referrer'], CARRIED_REFERRER_LIMIT);
  const company = isDict(value['company']) ? cleanCompanyDraft(value['company']) : undefined;
  return {
    decorText: text(value['decorText']),
    requestDecor: value['requestDecor'] === true,
    note: text(value['note']),
    comment: text(value['comment']),
    channel: value['channel'] === 'WHATSAPP' || value['channel'] === 'INSTAGRAM' ? value['channel'] : 'CALLCENTER',
    ...(referrer ? { referrer } : {}),
    attachReceipt: value['attachReceipt'] !== false,
    important: value['important'] === true,
    kaspiNow: value['kaspiNow'] === true,
    clientType: value['clientType'] === 'LEGAL' ? 'LEGAL' : 'INDIVIDUAL',
    // a legal entity could only be chosen (the draft that is older than the mark knew no other way to it)
    ...(value['clientTypeSet'] === true || value['clientType'] === 'LEGAL' ? { clientTypeSet: true as const } : {}),
    ...(company ? { company } : {}),
  };
}

export function parseDraft(json: string | null | undefined): OrderDraft | undefined {
  if (!json) {
    return undefined;
  }
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return undefined;
  }
  if (!isDict(raw) || raw['version'] !== DRAFT_VERSION) {
    return undefined;
  }
  const id = str(raw['id']);
  const userId = num(raw['userId']);
  const touchedAt = num(raw['touchedAt']);
  if (!id || !/^[A-Za-z0-9_-]+$/.test(id) || userId === undefined || touchedAt === undefined) {
    return undefined;
  }
  const recipient = isDict(raw['recipient']) ? raw['recipient'] : {};
  const rejection = cleanRejectionDraft(raw['rejection']);
  const step2 = parseCarried(raw['step2']);
  const lines = Array.isArray(raw['lines']) ? raw['lines'] : [];
  const toggles = Array.isArray(raw['disabledDiscounts']) ? raw['disabledDiscounts'] : [];
  return {
    version: DRAFT_VERSION,
    id,
    userId,
    touchedAt,
    client: parseParty(raw['client']),
    recipient: { ...parseParty(recipient), enabled: recipient['enabled'] === true },
    ...(typeof raw['callLinked'] === 'boolean' ? { callLinked: raw['callLinked'] } : {}),
    lines: lines.map(parseLine).filter((l): l is DraftLine => l !== undefined),
    receiving: parseReceiving(raw['receiving']),
    disabledDiscounts: toggles.map(parseToggle).filter((t): t is DiscountToggle => t !== undefined),
    ...(rejection ? { rejection } : {}),
    ...(step2 ? { step2 } : {}),
  };
}
